import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Snackbar,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useVideos } from '../hooks/useVideos';
import { watchStatusApi, watchlistApi, pollApi, watchLimitApi } from '../services/api';
import VideoCard from '../components/video/VideoCard';
import type { Video, WatchLimitStatus } from '../types';

type FilterType = 'unwatched' | 'watchlist' | 'all';

export default function LandingPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterType>('unwatched');
  const [daysBack] = useState(7);
  const [polling, setPolling] = useState(false);
  const [pollMessage, setPollMessage] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [progressTotal, setProgressTotal] = useState(0);
  const cancelStreamRef = useRef<(() => void) | null>(null);

  const [watchLimit, setWatchLimit] = useState<WatchLimitStatus | null>(null);
  const [watchLimitLoading, setWatchLimitLoading] = useState(true);
  const [activatingDay, setActivatingDay] = useState(false);
  const [pendingVideo, setPendingVideo] = useState<Video | null>(null);
  const [confirmDialogType, setConfirmDialogType] = useState<'weekly' | 'extra' | null>(null);
  // When polling ends, flash to 100% then reset
  useEffect(() => {
    if (!polling && progressTotal > 0) {
      setProgress(progressTotal);
      const t = setTimeout(() => {
        setProgress(0);
        setProgressTotal(0);
      }, 600);
      return () => clearTimeout(t);
    }
  }, [polling]);

  const { videos, loading, error, refresh } = useVideos({
    filter,
    daysBack,
  });

  const fetchWatchLimit = useCallback(async () => {
    try {
      const status = await watchLimitApi.getStatus();
      setWatchLimit(status);
    } catch (err) {
      console.error('Failed to fetch watch limit status:', err);
    } finally {
      setWatchLimitLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWatchLimit();

    // Periodically refresh watch limit status (e.g. when crossing 24:00 midnight)
    const interval = setInterval(fetchWatchLimit, 30000);
    return () => clearInterval(interval);
  }, [fetchWatchLimit]);

  const navigateToVideo = (videoId: string) => {
    try {
      const el = document.documentElement as HTMLElement & { webkitRequestFullscreen?(): void };
      if (el.requestFullscreen) {
        el.requestFullscreen().catch(() => {});
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      }
    } catch {
      // Ignore fullscreen errors on unsupported devices
    }
    navigate(`/watch/${videoId}`);
  };

  const handleSelectVideo = (video: Video) => {
    if (watchLimit?.isWatchDayToday) {
      navigateToVideo(video.id);
      return;
    }

    if (!watchLimit || !watchLimit.weeklyDayUsed) {
      setPendingVideo(video);
      setConfirmDialogType('weekly');
    }
  };

  const handleConfirmActivate = async () => {
    if (!confirmDialogType) return;
    try {
      setActivatingDay(true);
      const updated = await watchLimitApi.activate(confirmDialogType);
      setWatchLimit(updated);
      const targetVideo = pendingVideo;
      setConfirmDialogType(null);
      setPendingVideo(null);
      if (targetVideo) {
        navigateToVideo(targetVideo.id);
      }
    } catch (err) {
      setPollMessage(err instanceof Error ? err.message : 'Failed to activate watch day');
    } finally {
      setActivatingDay(false);
    }
  };

  const handleCancelDialog = () => {
    if (activatingDay) return;
    setConfirmDialogType(null);
    setPendingVideo(null);
  };

  const handleMarkWatched = async (videoId: string) => {
    try {
      await watchStatusApi.markWatched({ videoId, percentage: 100 });
      // Refresh the list to remove the watched video
      refresh();
    } catch (err) {
      console.error('Failed to mark video as watched:', err);
    }
  };

  const handleFilterChange = (_event: React.SyntheticEvent, newValue: FilterType) => {
    setFilter(newValue);
  };

  const handleAddToWatchlist = async (videoId: string) => {
    try {
      await watchlistApi.add(videoId);
      refresh();
    } catch (err) {
      console.error('Failed to add to watchlist:', err);
    }
  };

  const handleRefresh = () => {
    setPolling(true);
    setProgress(0);
    setProgressTotal(0);
    setPollMessage(null);

    const cancel = pollApi.pollAllStream(
      daysBack,
      (total) => {
        setProgressTotal(total);
      },
      ({ completed }) => {
        setProgress(completed);
      },
      (result) => {
        setPollMessage(result.message);
        setPolling(false);
        cancelStreamRef.current = null;
        setTimeout(() => refresh(), 1000);
      },
      (error) => {
        console.error('Poll stream error:', error);
        setPollMessage('Failed to fetch new videos from YouTube');
        setPolling(false);
        cancelStreamRef.current = null;
      },
    );

    cancelStreamRef.current = cancel;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Latest Videos
        </Typography>
        <Button
          variant="outlined"
          startIcon={polling ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
          sx={{ position: 'relative', overflow: 'hidden', pointerEvents: polling ? 'none' : undefined }}
        >
          <Box
            component="span"
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'primary.main',
              opacity: 0.2,
              width: progressTotal > 0 ? `${(progress / progressTotal) * 100}%` : '0%',
              transition: 'width 0.3s ease-out',
              pointerEvents: 'none',
            }}
          />
          {polling ? 'Fetching...' : 'Refresh from YouTube'}
        </Button>
      </Box>

      {/* Watch Day Status Banner */}
      {!watchLimitLoading && watchLimit && (
        <>
          {watchLimit.isWatchDayToday ? (
            <Alert
              icon={<AccessTimeIcon fontSize="inherit" />}
              severity="success"
              sx={{ mb: 3, alignItems: 'center' }}
              action={
                <Chip
                  label={`${watchLimit.extraDaysRemaining} / ${watchLimit.extraDaysLimit} extra days left this month`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              }
            >
              <Typography variant="subtitle2" component="span" sx={{ fontWeight: 600 }}>
                {watchLimit.activeDayType === 'extra' ? 'Extra Watch Day Active' : 'Weekly Watch Day Active'}
              </Typography>
              {' — '}
              You can watch videos today until 24:00 (any video started before 24:00 can still be finished).
            </Alert>
          ) : !watchLimit.weeklyDayUsed ? (
            <Alert
              icon={<EventAvailableIcon fontSize="inherit" />}
              severity="info"
              sx={{ mb: 3, alignItems: 'center' }}
              action={
                <Chip
                  label={`${watchLimit.extraDaysRemaining} / ${watchLimit.extraDaysLimit} extra days left this month`}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              }
            >
              Your weekly YouTube day (Mon–Sun) is available. Click any video below to start your watch day until 24:00.
            </Alert>
          ) : null}
        </>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Blocked State when Weekly Watch Day Already Used */}
      {!watchLimitLoading && watchLimit && !watchLimit.isWatchDayToday && watchLimit.weeklyDayUsed ? (
        <Paper sx={{ p: 6, textAlign: 'center', mb: 4 }}>
          <EventBusyIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            You already had your watch day this week
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto', mb: 3 }}>
            Your weekly watch day (Monday–Sunday) was used on{' '}
            <strong>{watchLimit.weeklyDayDate}</strong>. Your next regular watch day will be available starting Monday.
          </Typography>

          {watchLimit.extraDaysRemaining > 0 ? (
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<PlayCircleOutlineIcon />}
                onClick={() => setConfirmDialogType('extra')}
                disabled={activatingDay}
              >
                Use Extra Watch Day ({watchLimit.extraDaysRemaining} of {watchLimit.extraDaysLimit} remaining this month)
              </Button>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1.5 }}>
                Activating an extra watch day lets you watch videos today until 24:00.
              </Typography>
            </Box>
          ) : (
            <Alert severity="warning" sx={{ maxWidth: 520, mx: 'auto', mt: 2, textAlign: 'left' }}>
              Both of your extra watch days ({watchLimit.extraDaysLimit}/{watchLimit.extraDaysLimit}) have already been used up for this month.
            </Alert>
          )}
        </Paper>
      ) : (
        <>
          {/* Filter Tabs */}
          <Paper sx={{ mb: 3 }}>
            <Tabs value={filter} onChange={handleFilterChange} aria-label="video filter tabs">
              <Tab label="Unwatched" value="unwatched" />
              <Tab label="Watchlist" value="watchlist" />
              <Tab label="All Videos" value="all" />
            </Tabs>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Showing videos from the last {daysBack} days (sorted by channel stars)
              </Typography>
            </Box>
          </Paper>

          {/* Loading State */}
          {loading && videos.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
              <CircularProgress />
            </Box>
          ) : videos.length === 0 ? (
            <Paper sx={{ p: 8 }}>
              <Typography variant="h6" align="center" color="text.secondary">
                No videos found
              </Typography>
              <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 1 }}>
                {filter === 'unwatched'
                  ? 'All caught up! Try the Watchlist or All Videos tabs.'
                  : filter === 'watchlist'
                  ? 'Your watchlist is empty. Add videos from the Unwatched or All Videos tabs.'
                  : 'No videos available. Add channels to start tracking videos.'}
              </Typography>
            </Paper>
          ) : (
            <>
              {/* Video Grid */}
              <Grid container spacing={3}>
                {videos.map((video) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={video.id}>
                    <VideoCard
                      video={video}
                      onMarkWatched={handleMarkWatched}
                      onAddToWatchlist={handleAddToWatchlist}
                      showWatchlistButton={filter !== 'watchlist'}
                      onSelectVideo={handleSelectVideo}
                    />
                  </Grid>
                ))}
              </Grid>

              {/* Stats */}
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                sx={{ mt: 3 }}
              >
                Showing all {videos.length} videos
              </Typography>
            </>
          )}
        </>
      )}

      {/* Watch Day Confirmation Dialog */}
      <Dialog open={confirmDialogType !== null} onClose={handleCancelDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {confirmDialogType === 'extra'
            ? 'Use an Extra Watch Day?'
            : 'Start Your Weekly Watch Day?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialogType === 'extra' ? (
              <>
                Do you want to activate one of your monthly extra watch days for today? You will be able to watch videos until <strong>24:00</strong> (any video started before 24:00 can still be finished).
                <br />
                <br />
                After activating, you will have{' '}
                <strong>{Math.max(0, (watchLimit?.extraDaysRemaining ?? 1) - 1)}</strong> of{' '}
                <strong>{watchLimit?.extraDaysLimit ?? 2}</strong> extra watch days remaining this month.
              </>
            ) : (
              <>
                You get one YouTube day per week (Monday–Sunday). Do you want to start your watch day now
                {pendingVideo ? (
                  <>
                    {' '}and watch <strong>{pendingVideo.title}</strong>
                  </>
                ) : null}
                ?
                <br />
                <br />
                You will be able to watch videos until <strong>24:00</strong> tonight (if you start a video before 24:00, you can still finish watching it).
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelDialog} disabled={activatingDay}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmActivate}
            variant="contained"
            color="primary"
            disabled={activatingDay}
            autoFocus
          >
            {activatingDay
              ? 'Starting...'
              : confirmDialogType === 'extra'
              ? 'Use Extra Watch Day'
              : 'Start Watch Day'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Poll Success Message */}
      <Snackbar
        open={!!pollMessage}
        autoHideDuration={6000}
        onClose={() => setPollMessage(null)}
        message={pollMessage}
      />
    </Box>
  );
}
